import { Box, Grid, useMediaQuery, useTheme, Link } from '@mui/material';
import React, { useEffect, useState } from 'react';
import IFundTypeHistoryFlow from '../../../../interfaces/expertise/history/IFundTypeHistoryFlow';
import { GridRowsProp, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import CChipFund from '../../../../components/CChipFund';
import { useNavigate } from 'react-router-dom';
import InvestorRoutes from '../../../../InvestorRoutes';
import HAppRoutes from '../../../../../../app/features/routes/helpers/HAppRoutes';
import AppFormatDate from '../../../../../../app/features/format/AppFormatDate';
import AppFormatAmount from '../../../../../../app/features/format/AppFormatAmount';
import investmentFundTypeHistoryFlowLang from '../../langs/investmentFundTypeHistoryFlowLang';
import IFund from '../../../../interfaces/fund/IFund';
import CAppPaper from '../../../../../../app/components/CAppPaper';
import moment from 'moment';
import styleVariables from '../../../../../../app/styles/styleVariables';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { BoxFundItem } from '../../../../views/expertise/VInvestExpertisePortfolio';
import CAppDataGrid from '../../../../../../app/components/CAppDataGrid';

type CHistoryInflowOutflowProps = {
	listFunds: IFund[];
	fundTypeHistoryFlows: IFundTypeHistoryFlow | null | undefined;
};
const CHistoryInflowOutflow = (props: CHistoryInflowOutflowProps) => {
	const { listFunds, fundTypeHistoryFlows } = props;

	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [rows, setRows] = useState<GridRowsProp | null>(null);

	useEffect(() => {
		if (listFunds && fundTypeHistoryFlows && fundTypeHistoryFlows.funds) {
			setRows(
				_.orderBy(
					fundTypeHistoryFlows.funds
						.filter((f) => f.amount !== 0 && listFunds.some((fund) => fund.id === f.fundId))
						.map((x, index) => {
							const currentFund = listFunds.find((f) => f.id === x.fundId) as IFund;
							const currentFundInvestor = _.flatten(listFunds.map((f) => f.investors)).find(
								(i) => i?.company.id === x.investorId,
							);
							return {
								id: index,
								...x,
								fundName: currentFund.name,
								fundId: currentFund.id,
								currency: x.currency,
								fundIsRealized: currentFund.isRealized,
								fundTypeTag: currentFund.typeTag,
								typeName:
									x.typeTag && investmentFundTypeHistoryFlowLang[`type_${x.typeTag}`]
										? t(investmentFundTypeHistoryFlowLang[`type_${x.typeTag}`].tag)
										: '?',
								investor: currentFundInvestor ? currentFundInvestor.company.name : '',
							};
						}),
					['date'],
					['desc'],
				),
			);
		}
	}, [listFunds, fundTypeHistoryFlows, t]);

	return (
		<Box>
			{rows ? (
				isMobile ? (
					<CHistoryInflowOutflowMobile rows={rows} />
				) : (
					<CHistoryInflowOutflowDesktop rows={rows} />
				)
			) : (
				''
			)}
		</Box>
	);
};

const CHistoryInflowOutflowDesktop = (props: { rows: GridRowsProp }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { rows } = props;

	const columns: GridColDef[] = [
		{
			field: 'date',
			headerName: t(investmentFundTypeHistoryFlowLang.date.tag),
			width: 120,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'fundName',
			headerName: t(investmentFundTypeHistoryFlowLang.fund.tag),
			width: 280,

			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<CChipFund
						displayTooltip
						onClick={() =>
							navigate(
								HAppRoutes.getPath(InvestorRoutes.fund.tag, {
									investExpertiseTag: params.row.fundTypeTag,
									id: params.row.fundId.toString(),
								}),
							)
						}
						disabled={params.row.fundIsRealized}
						label={params.value}
					/>
				);
			},
		},
		{
			field: 'typeName',
			headerName: t(investmentFundTypeHistoryFlowLang.type.tag),
			width: 200,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => t(params.value),
		},
		{
			field: 'amount',
			headerName: t(investmentFundTypeHistoryFlowLang.amount.tag),
			width: 100,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return <AppFormatAmount value={params.value} addSpace currency={params.row.currency} />;
			},
		},
		{
			field: 'investor',
			headerName: t(investmentFundTypeHistoryFlowLang.investor.tag),
			width: 350,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => t(params.value),
		},
	];

	return (
		<>
			{rows ? (
				<Box className="expertise-inflow-outflow-table-desktop">
					<CAppDataGrid
						pageSize={10}
						getRowClassName={(params) => `row-status--${params.row.fundIsRealized}`}
						rows={rows}
						columns={columns}
						autoHeight
						rowsPerPageOptions={[10]}
					/>
				</Box>
			) : (
				''
			)}
		</>
	);
};

const CHistoryInflowOutflowMobile = (props: { rows: GridRowsProp }) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const { t } = useTranslation();
	const { rows } = props;

	const [accordionRows, setAccordionRows] = useState<
		| {
				id: number;
				isOpen: boolean;
		  }[]
		| null
	>(null);

	useEffect(() => {
		if (rows) {
			setAccordionRows(
				rows.map((row) => ({
					id: row.id,
					isOpen: false,
				})),
			);
		}
	}, [rows]);

	const handleToggleAccordionRows = (row: { id: string | number }) => {
		if (!accordionRows) return;
		setAccordionRows(
			accordionRows.map((x) => ({
				...x,
				isOpen: x.id === row.id ? !x.isOpen : x.isOpen,
			})),
		);
	};

	return (
		<>
			{rows ? (
				<Box className="expertise-inflow-outflow-table-mobile">
					{rows.map((row) => {
						return (
							<CAppPaper
								className="expertise-inflow-outflow-table-mobile-row"
								key={row.id}
								variant="outlined"
								sx={{
									marginBottom: '15px',
									backgroundColor:
										theme.palette.mode === 'dark' ? styleVariables.primaryColor : '#FFF',
									border: '1px solid white',
								}}
							>
								<Grid container spacing={3}>
									<Grid
										item
										xs={6}
										sx={{
											fontSize: '0.9rem',
											fontWeight: '400',
											color: theme.palette.primary.main,
											//maxHeight: '3rem',
											overflow: 'hidden',
										}}
									>
										{moment(row.date).format('DD/MM/YYYY')}
									</Grid>
									<Grid
										item
										xs={6}
										sx={{
											textAlign: 'right',
											fontSize: '0.9rem',
											fontWeight: '400',
											color: theme.palette.primary.main,
										}}
									>
										<AppFormatAmount value={row.amount} addSpace currency={row.currency} />
									</Grid>
								</Grid>
								<Box sx={{ marginTop: '6px' }}>
									<Link
										color="primary"
										sx={{
											position: 'relative',
											fontWeight: 600,
											fontSize: '0.875rem',
											lineHeight: '1.7',
										}}
										onClick={() => handleToggleAccordionRows(row as { id: string | number })}
									>
										Détails
										{accordionRows && accordionRows.some((i) => i.id === row.id && i.isOpen) ? (
											<KeyboardArrowUpOutlined
												sx={{
													marginTop: '0px',
													position: 'absolute',
													top: '50%',
													transform: 'translateY(-50%)',
												}}
											/>
										) : (
											<KeyboardArrowDownOutlined
												sx={{
													marginTop: '0px',
													position: 'absolute',
													top: '50%',
													transform: 'translateY(-50%)',
												}}
											/>
										)}
									</Link>
								</Box>
								{accordionRows && accordionRows.some((i) => i.id === row.id && i.isOpen) ? (
									<Box sx={{ marginTop: '6px' }}>
										<Grid container spacing={3}>
											<Grid item xs={12}>
												<BoxFundItem
													label={t(investmentFundTypeHistoryFlowLang.fund.tag)}
													value={
														<CChipFund
															onClick={() =>
																navigate(
																	HAppRoutes.getPath(InvestorRoutes.fund.tag, {
																		investExpertiseTag: row.fundTypeTag,
																		id: row.fundId.toString(),
																	}),
																)
															}
															disabled={row.fundIsRealized}
															label={row.fundName}
														/>
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<BoxFundItem
													label={t(investmentFundTypeHistoryFlowLang.type.tag)}
													value={t(row.typeName)}
												/>
											</Grid>

											<Grid item xs={12}>
												<BoxFundItem
													label={t(investmentFundTypeHistoryFlowLang.investor.tag)}
													value={row.investor}
												/>
											</Grid>
										</Grid>
									</Box>
								) : (
									''
								)}
							</CAppPaper>
						);
					})}
				</Box>
			) : (
				''
			)}
		</>
	);
};

export default CHistoryInflowOutflow;
